import React from 'react';
import '../styles/Select.css';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  // Value
  value: string | string[];
  onChange: (value: string | string[]) => void;

  // Options
  options: SelectOption[];
  placeholder?: string;

  // Behavior
  multiple?: boolean;
  disabled?: boolean;
  clearable?: boolean;

  // Validation
  error?: string | null;

  // Styling
  className?: string;
  style?: React.CSSProperties;

  // Accessibility
  id?: string;
  label?: string;
  'aria-label'?: string;
}

export const Select: React.FC<SelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  multiple = false,
  disabled = false,
  clearable = false,
  error,
  className = '',
  style,
  id,
  label,
  'aria-label': ariaLabel,
}) => {
  const selectId = id || (label ? `select-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (multiple) {
      const selectedOptions = Array.from(e.target.selectedOptions, (option) => option.value);
      onChange(selectedOptions);
    } else {
      onChange(e.target.value);
    }
  };

  const handleClear = () => {
    onChange(multiple ? [] : '');
  };

  const hasValue = multiple ? (value as string[]).length > 0 : value !== '';

  return (
    <div className="select-wrapper" style={style}>
      {label && (
        <label htmlFor={selectId} className="select-label">
          {label}
        </label>
      )}
      <div className="select-container">
        <select
          id={selectId}
          value={value}
          onChange={handleChange}
          multiple={multiple}
          disabled={disabled}
          aria-label={ariaLabel || label}
          aria-invalid={!!error}
          aria-describedby={error ? `${selectId}-error` : undefined}
          className={`select ${className} ${error ? 'select-error' : ''}`}
        >
          {!multiple && placeholder && (
            <option value="" disabled={!clearable}>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        {clearable && hasValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="select-clear"
            aria-label="Clear selection"
          >
            ×
          </button>
        )}
      </div>
      {error && (
        <div id={`${selectId}-error`} className="select-error-message" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};
